/** La tabla de una tarjeta: la vista exacta que se puede leer y citar.
 *  Existe siempre, y es el "relief" que el validador de paleta exige cuando una serie no llega
 *  a 3:1 de contraste. Ancho con scroll propio: el cuerpo de la página nunca scrollea en horizontal. */
import type { Encabezado, Idioma } from "../lib/tipos";
import { celda } from "../lib/formato";
import { t } from "../lib/i18n";
import { useEsMovil } from "../lib/movil";

const MAX_FILAS = 40;
/** En móvil cada fila ocupa varias líneas, así que ochenta filas serían un muro. */
const MAX_FILAS_MOVIL = 8;

export function Tabla({ filas, encabezados, idioma }: {
  filas: Record<string, unknown>[]; encabezados: Encabezado[]; idioma: Idioma;
}) {
  const txt = t(idioma);
  const movil = useEsMovil();
  if (!filas.length) return null;
  const cols: Encabezado[] = encabezados.length
    ? encabezados
    : Object.keys(filas[0] ?? {}).slice(0, 8).map((k) => ({ clave: k, es: k, en: k }));
  const visibles = filas.slice(0, movil ? MAX_FILAS_MOVIL : MAX_FILAS);

  // En móvil, UNA TARJETA POR FILA en vez de scroll lateral. Medido el 2026-09-09: la tabla de
  // `riesgo_percepcion` pedía 871 px de ancho en 257 visibles, o sea 3,4 pantallas de scroll
  // horizontal — y una celda de nombre de SKU sola medía 188 px. Apilado no hace falta scroll.
  //
  // Deja de ser un `<table>` a propósito: sin cabecera visible, la asociación celda-columna se
  // rompería y un lector de pantalla leería filas de números sueltos. Una lista de `<dl>` dice
  // lo que es —una lista de registros, cada uno con sus pares etiqueta/valor— y se lee bien.
  if (movil) {
    const [titulo, ...resto] = cols;
    return (
      <div className="tabla-envoltura">
        <ul className="tabla-apilada">
          {visibles.map((f, i) => (
            <li key={i}>
              {titulo && <p className="tabla-apilada-titulo">{celda(f[titulo.clave], idioma)}</p>}
              <dl>
                {resto.map((c) => (
                  <div key={c.clave}>
                    <dt>{idioma === "es" ? c.es : c.en}</dt>
                    <dd className={typeof f[c.clave] === "number" ? "num" : undefined}>{celda(f[c.clave], idioma)}</dd>
                  </div>
                ))}
              </dl>
            </li>
          ))}
        </ul>
        {filas.length > visibles.length && (
          <p className="tabla-pie">+{filas.length - visibles.length} {txt.filas} {txt.verMas}</p>
        )}
      </div>
    );
  }

  return (
    <div className="tabla-envoltura">
      <table className="tabla">
        <thead>
          <tr>{cols.map((c) => <th key={c.clave} scope="col">{idioma === "es" ? c.es : c.en}</th>)}</tr>
        </thead>
        <tbody>
          {visibles.map((f, i) => (
            <tr key={i}>
              {cols.map((c) => {
                const v = f[c.clave];
                const numerica = typeof v === "number";
                return <td key={c.clave} className={numerica ? "num" : undefined}>{celda(v, idioma)}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {filas.length > visibles.length && (
        <p className="tabla-pie">+{filas.length - visibles.length} {txt.filas} {txt.verMas}</p>
      )}
    </div>
  );
}
